/**
 * StrategySuggestionsTab — ranked option strategies for the chart's
 * order-confirm dialog (Round 67b).
 *
 * Receives the user's directional bias (from the BUY/SHORT click on the
 * chart), risk budget (Target Gain / SL Risk INR) and available margin,
 * then renders strategy cards backed by /v1/options/suggest.
 *
 * Each card shows the minimum info a trader needs to decide; Greeks are
 * tucked behind an accordion so newcomers aren't overwhelmed. Off-direction
 * candidates are collapsed under an "Override" group so the user can still
 * see payoffs for strategies opposite to their bias.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip,
  CircularProgress, Collapse, FormControlLabel, IconButton, Switch, TextField,
  Tooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import StarIcon from '@mui/icons-material/Star';
import toast from 'react-hot-toast';
import {
  optionsSuggestService, type SuggestedStrategy, type SuggestResponse,
} from '../../services/optionsSuggest.service';

interface Props {
  open: boolean;
  underlyingSymbol: string;
  /** Chart exchange — lets the server map BTCUSD → BTC, NIFTY 50 → NIFTY. */
  exchange?: string;
  /** Chart side mapped to bias. BUY → LONG, SHORT → SHORT. */
  direction: 'LONG' | 'SHORT';
  /** Risk budget in INR — pulled from the captured SL distance × lots. */
  riskInr: number;
  /** Available margin in INR — from brokerFunds.service. */
  availableMarginInr: number;
  isDark: boolean;
}

const fmt = (n: number, p = 0) => '₹' + Math.abs(Math.round(n * Math.pow(10, p)) / Math.pow(10, p)).toLocaleString('en-IN', { maximumFractionDigits: p });

const fitColor = (fit: number): string =>
  fit >= 70 ? '#2e7d32' : fit >= 50 ? '#1e88e5' : fit >= 30 ? '#ef6c00' : '#c62828';

const fitLabel = (fit: number): string =>
  fit >= 70 ? 'Top fit' : fit >= 50 ? 'Good fit' : fit >= 30 ? 'OK' : 'Override';

/** Interactive payoff curve with hover crosshair. Expiry and Today curves are
 *  overlaid with the zero baseline; break-evens render as orange dashed
 *  verticals and the current spot as a teal vertical. Mouse hover snaps to the
 *  nearest data column and shows the spot price + Expiry/Today P&L values via
 *  axis tick labels and a floating chip near the cursor. */
const PayoffChart: React.FC<{
  expiry: number[]; today: number[]; spotRange: number[];
  breakEvens: number[]; spot?: number;
  width?: number; height?: number; isDark: boolean;
}> = ({ expiry, today, spotRange, breakEvens, spot, width = 460, height = 180, isDark }) => {
  const PADDING_L = 56, PADDING_R = 12, PADDING_T = 10, PADDING_B = 24;
  const plotW = width - PADDING_L - PADDING_R;
  const plotH = height - PADDING_T - PADDING_B;
  const [hoverIdx, setHoverIdx] = React.useState<number | null>(null);
  const svgRef = React.useRef<SVGSVGElement>(null);

  if (!expiry.length || !spotRange.length) return null;

  const min = Math.min(...expiry, ...today, 0);
  const max = Math.max(...expiry, ...today, 0);
  const range = Math.max(1, max - min);
  const x = (i: number) => PADDING_L + (i / (expiry.length - 1)) * plotW;
  const y = (v: number) => PADDING_T + plotH - ((v - min) / range) * plotH;
  const pathOf = (arr: number[]) =>
    arr.map((v, i) => (i === 0 ? 'M' : 'L') + x(i).toFixed(1) + ',' + y(v).toFixed(1)).join(' ');

  // Closed area between expiry curve and zero — green above, red below.
  const zeroY = y(0);
  const greenArea = expiry.map((v, i) => v > 0
    ? `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`
    : `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${zeroY.toFixed(1)}`).join(' ')
    + ` L${x(expiry.length - 1).toFixed(1)},${zeroY.toFixed(1)} L${x(0).toFixed(1)},${zeroY.toFixed(1)} Z`;
  const redArea = expiry.map((v, i) => v < 0
    ? `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`
    : `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${zeroY.toFixed(1)}`).join(' ')
    + ` L${x(expiry.length - 1).toFixed(1)},${zeroY.toFixed(1)} L${x(0).toFixed(1)},${zeroY.toFixed(1)} Z`;

  // Y-axis ticks — show min, zero, max so user sees scale instantly.
  const yTicks = Array.from(new Set([min, 0, max].map(v => Math.round(v))))
    .sort((a, b) => a - b);
  // X-axis ticks — 5 evenly spaced spot values.
  const xTickIdx = [0, Math.floor(spotRange.length * 0.25), Math.floor(spotRange.length / 2),
                    Math.floor(spotRange.length * 0.75), spotRange.length - 1];

  const onMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = e.clientX - rect.left;
    if (px < PADDING_L || px > width - PADDING_R) { setHoverIdx(null); return; }
    const i = Math.max(0, Math.min(expiry.length - 1, Math.round(((px - PADDING_L) / plotW) * (expiry.length - 1))));
    setHoverIdx(i);
  };
  const onMouseLeave = () => setHoverIdx(null);

  const axis = isDark ? '#37474f' : '#cfd8dc';
  const axisText = isDark ? '#90a4ae' : '#607d8b';
  const inrFmt = (v: number) => (v >= 0 ? '+' : '') + Math.round(v).toLocaleString('en-IN');

  // Spot vertical (interpolated from spotRange to pixel x).
  const spotX = (() => {
    if (spot == null) return null;
    const lo = spotRange[0], hi = spotRange[spotRange.length - 1];
    if (spot < lo || spot > hi) return null;
    return PADDING_L + ((spot - lo) / (hi - lo)) * plotW;
  })();

  return (
    <Box sx={{ position: 'relative' }}>
      <svg ref={svgRef} width={width} height={height}
        onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}
        style={{ display: 'block', borderRadius: 4, background: isDark ? '#0f1924' : '#fafafa', cursor: 'crosshair' }}>
        {/* Green/red P&L areas under expiry curve */}
        <path d={greenArea} fill="#26a69a" opacity={isDark ? 0.18 : 0.13} />
        <path d={redArea}   fill="#ef5350" opacity={isDark ? 0.20 : 0.15} />
        {/* Y-axis */}
        <line x1={PADDING_L} y1={PADDING_T} x2={PADDING_L} y2={PADDING_T + plotH} stroke={axis} />
        {/* X-axis (zero P&L baseline doubles as it visually) */}
        <line x1={PADDING_L} y1={PADDING_T + plotH} x2={width - PADDING_R} y2={PADDING_T + plotH} stroke={axis} />
        {/* Zero P&L horizontal */}
        <line x1={PADDING_L} y1={zeroY} x2={width - PADDING_R} y2={zeroY} stroke={axis} strokeDasharray="3 3" />
        {/* Y tick labels (P&L INR) */}
        {yTicks.map((v, i) => (
          <g key={'yt' + i}>
            <line x1={PADDING_L - 3} y1={y(v)} x2={PADDING_L} y2={y(v)} stroke={axis} />
            <text x={PADDING_L - 6} y={y(v) + 3} fontSize="10" fill={axisText} textAnchor="end">{inrFmt(v)}</text>
          </g>
        ))}
        {/* X tick labels (spot) */}
        {xTickIdx.map(i => (
          <g key={'xt' + i}>
            <line x1={x(i)} y1={PADDING_T + plotH} x2={x(i)} y2={PADDING_T + plotH + 3} stroke={axis} />
            <text x={x(i)} y={PADDING_T + plotH + 14} fontSize="10" fill={axisText} textAnchor="middle">
              {Math.round(spotRange[i]).toLocaleString('en-IN')}
            </text>
          </g>
        ))}
        {/* Today curve (dashed grey) */}
        <path d={pathOf(today)}  fill="none" stroke={isDark ? '#b0bec5' : '#78909c'} strokeWidth={1.2} strokeDasharray="4 3" opacity={0.85} />
        {/* Expiry curve (bold blue) */}
        <path d={pathOf(expiry)} fill="none" stroke="#1e88e5" strokeWidth={2} />
        {/* Spot vertical (teal) */}
        {spotX != null && (
          <g>
            <line x1={spotX} y1={PADDING_T} x2={spotX} y2={PADDING_T + plotH} stroke="#00897b" strokeWidth={1.2} />
            <text x={spotX} y={PADDING_T - 2} fontSize="9" fill="#00897b" textAnchor="middle" fontWeight={700}>spot</text>
          </g>
        )}
        {/* Break-even verticals (orange dashed) */}
        {breakEvens.map((be, i) => {
          const lo = spotRange[0], hi = spotRange[spotRange.length - 1];
          if (be < lo || be > hi) return null;
          const px = PADDING_L + ((be - lo) / (hi - lo)) * plotW;
          return (
            <g key={'be' + i}>
              <line x1={px} y1={PADDING_T} x2={px} y2={PADDING_T + plotH} stroke="#ef6c00" strokeWidth={1} strokeDasharray="3 3" />
              <text x={px} y={PADDING_T + 8} fontSize="9" fill="#ef6c00" textAnchor="middle" fontWeight={700}>BE</text>
            </g>
          );
        })}
        {/* Crosshair */}
        {hoverIdx != null && (() => {
          const cx = x(hoverIdx);
          const expV = expiry[hoverIdx], todV = today[hoverIdx];
          return (
            <g>
              <line x1={cx} y1={PADDING_T} x2={cx} y2={PADDING_T + plotH} stroke={axisText} strokeDasharray="2 2" />
              <line x1={PADDING_L} y1={y(expV)} x2={width - PADDING_R} y2={y(expV)} stroke="#1e88e5" strokeDasharray="2 2" opacity={0.5} />
              <circle cx={cx} cy={y(expV)} r={3} fill="#1e88e5" />
              <circle cx={cx} cy={y(todV)} r={2.5} fill={isDark ? '#b0bec5' : '#78909c'} />
              {/* Crosshair value badges glued to axes */}
              <g>
                <rect x={cx - 30} y={PADDING_T + plotH + 1} width={60} height={14} fill={isDark ? '#1e2d3d' : '#37474f'} rx={2} />
                <text x={cx} y={PADDING_T + plotH + 11} fontSize="9.5" fill="#fff" textAnchor="middle" fontWeight={700}>
                  {Math.round(spotRange[hoverIdx]).toLocaleString('en-IN')}
                </text>
              </g>
              <g>
                <rect x={2} y={y(expV) - 7} width={PADDING_L - 6} height={14} fill="#1e88e5" rx={2} />
                <text x={PADDING_L - 9} y={y(expV) + 3.5} fontSize="9.5" fill="#fff" textAnchor="end" fontWeight={700}>{inrFmt(expV)}</text>
              </g>
            </g>
          );
        })()}
      </svg>
      {/* Floating tooltip with both P&L values; only shown on hover. */}
      {hoverIdx != null && (
        <Box sx={{
          position: 'absolute', top: 4, right: 4,
          bgcolor: isDark ? 'rgba(15,25,36,0.94)' : 'rgba(255,255,255,0.96)',
          border: `1px solid ${isDark ? '#1e2d3d' : '#e0e0e0'}`,
          borderRadius: 0.6, p: 0.6, fontSize: '0.7rem', minWidth: 130, pointerEvents: 'none',
        }}>
          <Box>Spot <strong>{Math.round(spotRange[hoverIdx]).toLocaleString('en-IN')}</strong></Box>
          <Box style={{ color: '#1e88e5' }}>Expiry P&L <strong>{inrFmt(expiry[hoverIdx])}</strong></Box>
          <Box style={{ color: isDark ? '#b0bec5' : '#78909c' }}>Today P&L <strong>{inrFmt(today[hoverIdx])}</strong></Box>
        </Box>
      )}
      {/* Mini legend */}
      <Box sx={{ display: 'flex', gap: 1.5, fontSize: '0.66rem', color: 'text.secondary', mt: 0.3, ml: `${PADDING_L}px` }}>
        <Box><span style={{ display: 'inline-block', width: 12, height: 2, background: '#1e88e5', verticalAlign: 'middle', marginRight: 3 }} />Expiry</Box>
        <Box><span style={{ display: 'inline-block', width: 12, height: 0, borderTop: `2px dashed ${isDark ? '#b0bec5' : '#78909c'}`, verticalAlign: 'middle', marginRight: 3 }} />Today</Box>
        {spot != null && <Box><span style={{ display: 'inline-block', width: 2, height: 10, background: '#00897b', verticalAlign: 'middle', marginRight: 4 }} />Spot</Box>}
        {breakEvens.length > 0 && <Box><span style={{ display: 'inline-block', width: 2, height: 10, background: '#ef6c00', verticalAlign: 'middle', marginRight: 4 }} />Break-even</Box>}
      </Box>
    </Box>
  );
};

const StrategyCard: React.FC<{
  card: SuggestedStrategy; isDark: boolean; available: number;
  /** Spot price for the underlying — drawn as a teal vertical on the payoff
   *  chart so the user instantly sees "we're here right now". */
  spot?: number;
  onExecute: (lots: number) => void; executing?: boolean;
}> = ({ card, isDark, available, spot, onExecute, executing }) => {
  const [lots, setLots] = useState<number>(1);
  const [greeksOpen, setGreeksOpen] = useState(false);
  const marginPct = available > 0 ? (card.marginEstimate * lots / available) * 100 : null;
  const marginColor = marginPct == null ? '#9e9e9e' : marginPct > 80 ? '#c62828' : marginPct > 50 ? '#ef6c00' : '#2e7d32';
  const fitC = fitColor(card.fitScore);

  return (
    <Box sx={{
      mt: 1, p: 1, borderRadius: 1.2,
      bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)',
      border: `1px solid ${isDark ? '#1e2d3d' : '#e0e0e0'}`,
    }}>
      {/* Title row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap', mb: 0.4 }}>
        {card.fitScore >= 70 && <StarIcon sx={{ fontSize: 14, color: fitC }} />}
        <Typography sx={{ fontWeight: 800, fontSize: '0.86rem' }}>{card.name}</Typography>
        <Chip size="small" label={`${fitLabel(card.fitScore)} ${card.fitScore}`}
          sx={{ height: 18, fontSize: '0.66rem', fontWeight: 700, bgcolor: fitC, color: '#fff' }} />
        <Chip size="small" label={card.direction}
          sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700,
            bgcolor: card.direction === 'LONG' ? '#2e7d32' : card.direction === 'SHORT' ? '#c62828' : '#5e35b1', color: '#fff' }} />
        {card.riskShape === 'undefined' && (
          <Chip size="small" label="Undefined risk"
            sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: '#fbc02d', color: '#212121' }} />
        )}
      </Box>

      {/* Legs */}
      <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary', mb: 0.4 }}>
        {card.legs.map((l, i) => (
          <span key={i}>
            <span style={{ color: l.action === 'BUY' ? '#2e7d32' : '#c62828', fontWeight: 700 }}>{l.action}</span>
            {' '}{l.strike}{l.optionType} @ {fmt(l.ltp, 1)}{i < card.legs.length - 1 ? '  ·  ' : ''}
          </span>
        ))}
      </Typography>

      {/* Key numbers grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 0.4, fontSize: '0.72rem', mb: 0.6 }}>
        <Box>
          <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary' }}>Max Win</Typography>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#2e7d32' }}>{fmt(card.maxProfit * lots)}</Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary' }}>Max Loss</Typography>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#c62828' }}>
            {card.riskShape === 'undefined' ? 'Unbounded' : fmt(card.maxLoss * lots)}
          </Typography>
        </Box>
        {/* Chance of profit, integrated against the option market's own implied distribution —
            NOT the old "Win Prob", which counted profitable points on the simulated spot grid with
            every price weighted equally and scored a long strangle at 96%. When the honest figure
            cannot be computed (no ATM IV, no time to expiry) this shows a dash: the range fraction
            is not a fallback for it, it is the thing being replaced. */}
        <Box>
          <Tooltip arrow title={
            card.probabilityOfProfit == null
              ? 'Needs the ATM implied volatility from the chain and a live expiry to compute. Shown as a dash rather than estimated.'
              : `Probability this finishes profitable under the risk-neutral distribution implied by the option market's own volatility — the same distribution these premiums were priced from.${card.expectedPayoff != null ? ` Probability-weighted average outcome: ${card.expectedPayoff > 0 ? '+' : ''}${card.expectedPayoff}.` : ''}`
          }>
            <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary', cursor: 'help' }}>
              Chance of profit
            </Typography>
          </Tooltip>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 800 }}>
            {card.probabilityOfProfit == null ? '—' : `${card.probabilityOfProfit}%`}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary' }}>Margin</Typography>
          <Tooltip title={marginPct != null ? `${marginPct.toFixed(0)}% of available` : ''}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: marginColor }}>
              {fmt(card.marginEstimate * lots)}{marginPct != null && <span style={{ fontSize: '0.66rem', marginLeft: 2 }}>({marginPct.toFixed(0)}%)</span>}
            </Typography>
          </Tooltip>
        </Box>
      </Box>

      {/* Break-evens */}
      {card.breakEvens.length > 0 && (
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 0.4 }}>
          Break-even: <strong>{card.breakEvens.map(b => b.toLocaleString('en-IN')).join(' / ')}</strong>
        </Typography>
      )}

      {/* Rationale + fits/warnings */}
      <Typography sx={{ fontSize: '0.72rem', color: 'text.primary', mb: 0.4, lineHeight: 1.35 }}>
        {card.rationale}
      </Typography>
      {(card.warnings.length > 0 || card.fits.length > 0) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mb: 0.6 }}>
          {card.fits.slice(1, 3).map((f, i) => (
            <Chip key={'f' + i} size="small" label={'✓ ' + f}
              sx={{ height: 17, fontSize: '0.62rem', bgcolor: 'transparent', border: '1px solid #2e7d32', color: '#2e7d32' }} />
          ))}
          {card.warnings.slice(0, 2).map((w, i) => (
            <Chip key={'w' + i} size="small" label={'⚠ ' + w}
              sx={{ height: 17, fontSize: '0.62rem', bgcolor: 'transparent', border: '1px solid #ef6c00', color: '#ef6c00' }} />
          ))}
        </Box>
      )}

      {/* Payoff chart with crosshair (R67f) */}
      <PayoffChart expiry={card.payoffData.expiry.map(v => v * lots)}
        today={card.payoffData.today.map(v => v * lots)}
        spotRange={card.payoffData.spotRange}
        breakEvens={card.breakEvens} spot={spot} isDark={isDark} />

      {/* Greeks accordion (hidden by default) */}
      <Accordion expanded={greeksOpen} onChange={(_, v) => setGreeksOpen(v)}
        disableGutters elevation={0}
        sx={{ mt: 0.4, '&:before': { display: 'none' }, bgcolor: 'transparent' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}
          sx={{ minHeight: 28, p: 0, '& .MuiAccordionSummary-content': { my: 0 } }}>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Show Greeks</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0, pt: 0.4 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0.6, fontSize: '0.72rem' }}>
            <Box>Δ <strong>{(card.greeks.delta * lots).toFixed(2)}</strong></Box>
            <Box>θ <strong>{fmt(card.greeks.theta * lots, 0)}/day</strong></Box>
            <Box>Vega <strong>{fmt(card.greeks.vega * lots, 0)}</strong></Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Lots + Execute */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.6, justifyContent: 'flex-end' }}>
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Lots</Typography>
        <TextField size="small" type="number" value={lots}
          onChange={e => { const v = parseInt(e.target.value, 10); setLots(Number.isFinite(v) && v > 0 ? v : 1); }}
          inputProps={{ min: 1, style: { fontSize: '0.78rem', padding: '3px 6px', width: 48 } }} />
        <Button variant="contained" size="small" disabled={!!executing}
          onClick={() => onExecute(lots)}
          sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.74rem',
            bgcolor: fitC, '&:hover': { filter: 'brightness(0.92)', bgcolor: fitC } }}>
          {executing ? '…' : 'Execute Basket ▶'}
        </Button>
      </Box>
    </Box>
  );
};

const StrategySuggestionsTab: React.FC<Props> = ({ open, underlyingSymbol, exchange, direction, riskInr, availableMarginInr, isDark }) => {
  const [loading, setLoading] = useState(false);
  const [executingId, setExecutingId] = useState<string | null>(null);
  const [resp, setResp] = useState<SuggestResponse | null>(null);
  const [allowUndefined, setAllowUndefined] = useState(false);
  const [showOverride, setShowOverride] = useState(false);
  const [showOk, setShowOk] = useState(false);
  const [showDropped, setShowDropped] = useState(false);
  /** Number of bootstrap-retry attempts already made for this dialog open.
   *  Capped at MAX_BOOTSTRAP_RETRIES to prevent indefinite polling for a
   *  symbol Delta/Zerodha don't actually have an options chain for. */
  const [retryCount, setRetryCount] = useState(0);
  const MAX_BOOTSTRAP_RETRIES = 9; // 9 × 20s ≈ 3 minutes

  const fetchSuggestions = React.useCallback(async () => {
    if (!underlyingSymbol) return;
    setLoading(true);
    const r = await optionsSuggestService.suggest({
      underlyingSymbol, exchange, direction, riskInr, availableMarginInr,
      horizonDays: 7, allowUndefinedRisk: allowUndefined,
    });
    setResp(r);
    setLoading(false);
  }, [underlyingSymbol, exchange, direction, riskInr, availableMarginInr, allowUndefined]);

  useEffect(() => {
    if (!open) return;
    setRetryCount(0); // fresh open — reset the retry budget
    fetchSuggestions();
  }, [open, fetchSuggestions]);

  // Bootstrapping auto-retry — when the backend says "auto-registering, first
  // chain in ~30-60s", quietly poll every 20s for up to MAX_BOOTSTRAP_RETRIES.
  useEffect(() => {
    if (!open) return;
    if (!resp?.bootstrapping) return;
    if (retryCount >= MAX_BOOTSTRAP_RETRIES) return;
    const t = window.setTimeout(() => {
      setRetryCount(c => c + 1);
      fetchSuggestions();
    }, 20_000);
    return () => window.clearTimeout(t);
  }, [open, resp, retryCount, fetchSuggestions]);

  const { top, others, override } = useMemo(() => {
    const out: { top: SuggestedStrategy[]; others: SuggestedStrategy[]; override: SuggestedStrategy[] } = { top: [], others: [], override: [] };
    if (!resp?.strategies) return out;
    for (const s of resp.strategies) {
      if (!s.matchesDirection && s.direction !== 'NEUTRAL') out.override.push(s);
      else if (s.fitScore >= 60) out.top.push(s);
      else out.others.push(s);
    }
    return out;
  }, [resp]);

  const handleExecute = async (card: SuggestedStrategy, lots: number) => {
    setExecutingId(card.id);
    const r = await optionsSuggestService.execute({
      underlyingSymbol, exchange, direction, riskInr, availableMarginInr,
      horizonDays: 7, allowUndefinedRisk: allowUndefined,
      strategyId: card.id, lots,
    });
    setExecutingId(null);
    if (r.ok && r.basketId) {
      toast.success(`Basket #${r.basketId} created — complete leg-by-leg in Options Lab.`);
    } else {
      toast.error(r.reason || 'Failed to create basket');
    }
  };

  if (!underlyingSymbol) return null;

  return (
    <Box sx={{ pt: 1 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.6 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', fontSize: '0.74rem' }}>
          {resp?.underlying && (
            <>
              <Box><span style={{ color: '#90a4ae' }}>Spot </span><strong>{resp.underlying.spotPrice.toLocaleString('en-IN')}</strong></Box>
              <Box><span style={{ color: '#90a4ae' }}>Expiry </span><strong>{resp.underlying.expiry}</strong></Box>
              <Box><span style={{ color: '#90a4ae' }}>ATM </span><strong>{resp.underlying.atmStrike.toLocaleString('en-IN')}</strong></Box>
              {resp.underlying.ivRank30d != null && (
                <Tooltip title={
                  resp.underlying.ivRank30d >= 60 ? 'High IV — premium-selling shines' :
                  resp.underlying.ivRank30d <= 30 ? 'Low IV — premium-buying favoured' :
                  'Mid IV — vega-neutral spreads preferred'
                }>
                  <Box><span style={{ color: '#90a4ae' }}>IVR30 </span>
                    <strong style={{ color: resp.underlying.ivRank30d >= 60 ? '#c62828' : resp.underlying.ivRank30d <= 30 ? '#2e7d32' : '#ef6c00' }}>
                      {resp.underlying.ivRank30d.toFixed(0)}
                    </strong>
                  </Box>
                </Tooltip>
              )}
              {resp.underlying.hv30d != null && (
                <Box><span style={{ color: '#90a4ae' }}>HV30 </span><strong>{resp.underlying.hv30d.toFixed(1)}%</strong></Box>
              )}
            </>
          )}
        </Box>
        <IconButton size="small" onClick={fetchSuggestions} disabled={loading}>
          <RefreshIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <FormControlLabel
        control={<Switch size="small" checked={allowUndefined} onChange={e => setAllowUndefined(e.target.checked)} />}
        label={<Typography sx={{ fontSize: '0.72rem' }}>Include undefined-risk strategies (Short Straddle)</Typography>}
      />

      {loading && (
        <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={28} /></Box>
      )}

      {!loading && resp && !resp.ok && resp.bootstrapping && (
        <Alert severity="info" icon={<CircularProgress size={16} />}
          sx={{ mt: 1, fontSize: '0.78rem' }}
          action={<Button size="small" onClick={fetchSuggestions} sx={{ textTransform: 'none', fontSize: '0.7rem' }}>Retry now</Button>}>
          {resp.reason}
          {retryCount > 0 && (
            <Typography component="span" sx={{ ml: 0.5, fontSize: '0.7rem', color: 'text.secondary' }}>
              (retry {retryCount}/{MAX_BOOTSTRAP_RETRIES})
            </Typography>
          )}
          {retryCount >= MAX_BOOTSTRAP_RETRIES && (
            <Typography sx={{ mt: 0.4, fontSize: '0.72rem', color: '#c62828' }}>
              Still no chain data after {MAX_BOOTSTRAP_RETRIES} retries — check the FNO module to confirm {underlyingSymbol} has live options on this exchange.
            </Typography>
          )}
        </Alert>
      )}
      {!loading && resp && !resp.ok && !resp.bootstrapping && (
        <Alert severity="info" sx={{ mt: 1, fontSize: '0.78rem' }}>{resp.reason}</Alert>
      )}

      {!loading && resp?.ok && (
        <>
          {/* Top recommendations — only shown when we actually have hits. */}
          {top.length > 0 && (
            <Box>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 1, fontWeight: 700, letterSpacing: 0.3 }}>
                RECOMMENDED
              </Typography>
              {top.map(c => (
                <StrategyCard key={c.id} card={c} isDark={isDark} available={availableMarginInr}
                  spot={resp.underlying?.spotPrice}
                  onExecute={(lots) => handleExecute(c, lots)} executing={executingId === c.id} />
              ))}
            </Box>
          )}

          {/* "No top fit" — only when no top AND we have alternatives below. */}
          {top.length === 0 && (others.length > 0 || override.length > 0) && (
            <Alert severity="info" sx={{ mt: 1, fontSize: '0.78rem' }}>
              No top-fit strategy for current IV / margin / risk inputs. Check the alternatives below.
            </Alert>
          )}

          {/* All three groups empty AND we have considered-but-dropped data.
              Don't lie about "alternatives below" when there are none. */}
          {top.length === 0 && others.length === 0 && override.length === 0
            && (resp.considered?.length || 0) > 0 && (
            <Alert severity="warning" sx={{ mt: 1, fontSize: '0.78rem' }}>
              We considered <strong>{resp.considered?.length}</strong> strategies for {underlyingSymbol}, but
              none could be built into a tradeable card. Common causes: very wide bid/ask spreads on
              the chosen expiry, available margin too low for any structure, or required strikes
              missing from the snapshot. See details below.
            </Alert>
          )}

          {/* Same as above, fallback when even `considered` is empty (shouldn't normally happen). */}
          {top.length === 0 && others.length === 0 && override.length === 0
            && (resp.considered?.length || 0) === 0 && (
            <Alert severity="warning" sx={{ mt: 1, fontSize: '0.78rem' }}>
              No strategy candidates for {underlyingSymbol} on the {resp.underlying?.expiry || 'current'} expiry.
              Try a later expiry or wait for the next chain refresh.
            </Alert>
          )}

          {/* Other matching */}
          {others.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Button size="small" onClick={() => setShowOk(s => !s)} sx={{ textTransform: 'none', fontSize: '0.7rem' }}>
                {showOk ? '▾' : '▸'} Other matching strategies ({others.length})
              </Button>
              <Collapse in={showOk}>
                {others.map(c => (
                  <StrategyCard key={c.id} card={c} isDark={isDark} available={availableMarginInr}
                    onExecute={(lots) => handleExecute(c, lots)} executing={executingId === c.id} />
                ))}
              </Collapse>
            </Box>
          )}

          {/* Off-direction override */}
          {override.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Button size="small" onClick={() => setShowOverride(s => !s)} sx={{ textTransform: 'none', fontSize: '0.7rem', color: '#c62828' }}>
                {showOverride ? '▾' : '▸'} Off-direction — override at your own risk ({override.length})
              </Button>
              <Collapse in={showOverride}>
                {override.map(c => (
                  <StrategyCard key={c.id} card={c} isDark={isDark} available={availableMarginInr}
                    onExecute={(lots) => handleExecute(c, lots)} executing={executingId === c.id} />
                ))}
              </Collapse>
            </Box>
          )}

          {/* Transparency panel — always show when there were drops, regardless
              of whether top/others/override exist. Collapsed by default so it
              doesn't clutter the happy path. */}
          {(resp.considered?.length || 0) > 0 && (
            <Box sx={{ mt: 1 }}>
              <Button size="small" onClick={() => setShowDropped(s => !s)} sx={{ textTransform: 'none', fontSize: '0.7rem', color: 'text.secondary' }}>
                {showDropped ? '▾' : '▸'} Considered &amp; not shown ({resp.considered!.length})
              </Button>
              <Collapse in={showDropped}>
                <Box sx={{ mt: 0.4, p: 0.8, borderRadius: 1,
                  bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                  border: `1px solid ${isDark ? '#1e2d3d' : '#e0e0e0'}` }}>
                  {resp.considered!.map(c => (
                    <Typography key={c.id} sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 0.2 }}>
                      <strong style={{ color: 'inherit' }}>{c.name}</strong> ({c.direction.toLowerCase()}) — {c.reason}
                    </Typography>
                  ))}
                </Box>
              </Collapse>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default StrategySuggestionsTab;
